import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { LayoutDashboard, FolderOpen, User, Settings, Users, LogOut, Menu } from 'lucide-react';
import Header from '../../components/ui/Header';
import WelcomeHeader from './components/WelcomeHeader';
import ToolCard from './components/ToolCard';

import FlyingTabs from './components/FlyingTabs';

const MainDashboard = () => {
  const { userProfile, refreshProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [currentLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [featureFlags, setFeatureFlags] = useState({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchFeatureFlags();
  }, []);

  useEffect(() => {
    const syncSubscription = async () => {
      if (searchParams.get('payment') !== 'success') return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-subscription`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
          }
        );

        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Failed to sync subscription');

        await refreshProfile();

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('payment');
        setSearchParams(nextParams, { replace: true });
      } catch (error) {
        console.error('Subscription sync error:', error);
      }
    };

    syncSubscription();
  }, [refreshProfile, searchParams, setSearchParams]);

  const fetchFeatureFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('key, value')
        .like('key', 'feature_%');
      if (error) throw error;
      const flags = {};
      (data || []).forEach(row => { flags[row.key] = row.value === 'true'; });
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Error fetching feature flags:', err);
    }
  };

  const userInfo = {
    name: "John Doe",
    subscriptionTier: "pro",
    usageStats: {
      remaining: 850,
      total: 1000
    }
  };

  const getUserInitials = (name) => {
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase() || 'JD';
  };

  const accountMenuItems = [
  { icon: User, label: 'View profile', action: () => navigate('/user-profile-settings') },
  { icon: Settings, label: 'Manage account', action: () => navigate('/user-profile-settings') },
  { icon: Users, label: 'Join our community', action: () => window.open('https://community.example.com', '_blank') },
  { icon: LogOut, label: 'Sign out', action: () => navigate('/login') }];


  const designTools = [
  {
    id: 1,
    name: "Moodboard to Room",
    image: "/assets/images/moodboard-room-rendered.png",
    hoverImage: "/assets/images/moodboard-collage.png",
    imageAlt: "Modern interior design with brown leather sofa, wooden coffee table, and elegant pendant lighting",
    icon: "Layers",
    path: "/moodboard-to-3d-room",
    featureKey: "feature_moodboard_3d",
    category: "Interior Concept",
    description: "Combine single or multiple moodboards and room layout to create a unified, AI-generated 3D interior.",
    toolsSummary: "Moodboard-Only Room Generation • Full Layout Integration • Room Size Configuration • Multi-Image Upload"
  },
  {
    id: 2,
    name: "CAD Based Mood Room Generator",
    image: "/assets/images/cad-room-rendered.png",
    hoverImage: "/assets/images/cad-hover-new.png",
    imageAlt: "CAD-based mood room generator showing technical drawings transforming into realistic interior designs",
    icon: "Sparkles",
    path: "/cad-based-mood-room-generator",
    featureKey: "feature_cad_mood_room",
    category: "Interior Concept",
    description: "Generate stunning interior designs using CAD-based mood room technology with AI-powered design generation.",
    toolsSummary: "Structure-Preserving Render • Top-Down & Frontal Views"
  },
  {
    id: 3,
    name: "Sketch to 3D Interior",
    image: "/assets/images/sketch-room-rendered.png",
    hoverImage: "/assets/images/sketch-pencil-drawing.png",
    imageAlt: "Elegant bedroom interior with paneled walls, green accent chair, and brass coffee table",
    icon: "Pencil",
    path: "/sketch-to-interior-render",
    featureKey: "feature_sketch_interior",
    category: "Interior Concept",
    description: "Upload a sketch and mood images to create a fully realized 3D interior.",
    toolsSummary: "Sketch Outline Preservation • Mood Palette Application • Surface Material Mapping • Hand-Drawn to Photorealistic"
  },
  {
    id: 4,
    name: "Space Planner",
    image: "/assets/images/space-planner-default.png",
    hoverImage: "/assets/images/space-planner-hover.png",
    imageAlt: "Split-view comparison showing wireframe CAD floor plan transforming into realistic 3D rendered interior space",
    icon: "Box",
    path: "/cad-space-planner",
    featureKey: "feature_space_planner",
    category: ["Interior Concept", "Detail Editing"],
    description: "Re-plan furniture layout and room allocation within rendered or architectural floor plans.",
    toolsSummary: "Whole Room Reorganization • Specific Zone Editing • Zone Swap • Wall Border Override • CAD & Rendered Plan Support"
  },
  {
    id: 5,
    name: "Room Variation Generator",
    image: "/assets/images/variation-room-default.png",
    hoverImage: "/assets/images/variation-room-hover.png",
    imageAlt: "Minimalist bedroom with forest mural wallpaper and warm neutral tones",
    icon: "Sparkles",
    path: "/room-variation-generator",
    featureKey: "feature_room_variation",
    category: "Interior Concept",
    description: "Generate new interior design variations from completed or existing project references.",
    toolsSummary: "Pinterest Reference Import • Room Size Configuration • Style-Consistent Alternatives"
  },
  {
    id: 6,
    name: "Combine Interior Styles",
    image: "/assets/images/combine-styles-collage.png",
    hoverImage: "/assets/images/combine-styles-rendered.png",
    imageAlt: "Three-panel comparison showing interior style combination process from initial concepts to final blended design result",
    icon: "Palette",
    path: "/combine-interior-styles",
    featureKey: "feature_combine_styles",
    category: "Interior Concept",
    description: "Upload two different style references to blend them into a unique design.",
    toolsSummary: "Dual Style Blending • Furniture-to-Style Assignment • Custom Furniture Input • Pinterest Reference Import"
  },
  {
    id: 7,
    name: "Furniture and Materials Editing",
    image: "/assets/images/furniture-editing-default.png",
    hoverImage: "/assets/images/furniture-editing-hover.png",
    imageAlt: "Chrome-framed lounge chair with brown leather cushions in a minimalist interior space",
    icon: "Box",
    path: "/furniture-editing",
    featureKey: "feature_furniture_materials",
    category: "Detail Editing",
    description: "Transform buildings and architectural designs into miniature white 3D printed models.",
    toolsSummary: "Material Change • Realism & Quality Enhancement • Object & Material Placement • Object & Material Replacement • Object Movement • Object Removal • Texture Extraction • Isolate on Background"
  },
  {
    id: 8,
    name: "Freestyle",
    image: "/assets/images/freestyle-default.jpg",
    hoverImage: "/assets/images/freestyle-hover.jpg",
    imageAlt: "Coming soon - Freestyle interior design tool for creative freedom",
    icon: "Sparkles",
    path: "/freestyle-field",
    featureKey: "feature_freestyle",
    category: "Interior Concept",
    description: "Create unique interior designs with complete creative freedom and flexibility.",
    toolsSummary: "Text-to-Image Generation • Optional Reference Images • Batch Generation • Freeform Prompt Control"
  },
  {
    id: 9,
    name: "Site Concept",
    image: "/assets/images/site-concept-hover.png",
    hoverImage: "/assets/images/site-concept-default.png",
    imageAlt: "Site Concept tool for architectural site planning and visualization",
    icon: "Layers",
    path: "/site-concept",
    featureKey: "feature_site_concept",
    category: "Architecture",
    description: "Integrate building onto reference land as unified photorealistic composition.",
    toolsSummary: "Site-Building Integration • Lighting & Season Control • Floor Count Configuration • Sky & Weather Conditions"
  },
  {
    id: 10,
    name: "Ground-Up Complete Room Design",
    image: "/assets/images/complete-room-design-hover.png",
    hoverImage: "/assets/images/complete-room-design-default.png",
    imageAlt: "Ground-Up Complete Room Design tool for comprehensive interior creation",
    icon: "Box",
    path: "/complete-room-design",
    featureKey: "feature_ground_up_room",
    category: "Interior Concept",
    description: "Furnish empty rooms from scratch using separate object and material references.",
    toolsSummary: "Venue Shell Preservation • Furniture Piece Integration • Surface Material Application • Clear Room Toggle"
  }];

  // Filter tools: admin เห็นทุก tool (พร้อม disabled badge), user ทั่วไปซ่อน tool ที่ปิด
  const hasFlags = Object.keys(featureFlags).length > 0;
  const enabledTools = isAdmin
    ? designTools.map(tool => ({ ...tool, disabled: hasFlags && featureFlags[tool.featureKey] === false }))
    : hasFlags
      ? designTools.filter(tool => featureFlags[tool.featureKey] !== false)
      : designTools;

  const filteredTools = selectedCategory === 'all'
    ? enabledTools
    : enabledTools.filter(tool => {
        if (Array.isArray(tool?.category)) return tool.category.includes(selectedCategory);
        return tool?.category === selectedCategory;
      });

  return (
    <>
      <Helmet>
        <title>Main Dashboard - Roomwork.ai</title>
        <meta name="description" content="Access professional AI-powered interior design tools and manage your creative projects from your central dashboard" />
      </Helmet>
      <div className="min-h-screen bg-background overflow-x-hidden">
        <Header
          remainingCredits={userInfo?.usageStats?.remaining}
          monthlyLimit={userInfo?.usageStats?.total} />

        {/* Mobile Navigation Bar - Matching Desktop Style */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-[100] px-3 pt-3">
          <div className="flex items-center justify-between h-12 bg-white/10 backdrop-blur-sm rounded-3xl px-4">
            {/* Left: Logo + Hamburger */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.dispatchEvent(new Event('toggle-mobile-menu'))}
                className="flex items-center gap-2 transition-all duration-300">
                <div className="w-7 h-7 rounded-[10px] overflow-hidden shadow-lg flex-shrink-0">
                  <img
                    src="/assets/images/tg_image_1314028779-1770779087120.png"
                    alt="Roomwork.ai Logo"
                    className="w-full h-full object-cover" />
                </div>
                <span className="font-bold text-[rgba(255,252,239,1)] text-sm">Roomwork.ai</span>
              </button>
            </div>

            {/* Center: Navigation */}
            <nav className="flex items-center gap-4">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-2 py-1.5 transition-all duration-300 ${
                activeTab === 'dashboard' ? 'text-orange-500 font-bold' : 'text-white/70 hover:text-white font-normal'}`
                }>
                <span className="text-xs text-[rgba(255,252,239,1)]">Dashboard</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('projects');
                  navigate('/project-history');
                }}
                className={`px-2 py-1.5 transition-all duration-300 ${
                activeTab === 'projects' ? 'text-orange-500 font-bold' : 'text-white/70 hover:text-white font-normal'}`
                }>
                <span className="text-xs text-[rgba(255,252,239,1)]">Projects</span>
              </button>
            </nav>

            {/* Right: Avatar Circle */}
            <button
              onClick={() => window.dispatchEvent(new Event('toggle-mobile-menu'))}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 overflow-hidden flex-shrink-0 bg-white/5 border border-white/10">
              <User className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
        
        <main className="container mx-auto px-4 py-8 mt-4 lg:mt-20 pb-[20vh]">
          <div className="space-y-3 lg:mt-0 mt-8">
            <WelcomeHeader
              userName={userInfo?.name}
              subscriptionTier={userInfo?.subscriptionTier}
              usageStats={userInfo?.usageStats} />

            {/* Flying Tabs Section - Between Header Navigation and AI Tools */}
            <section>
              <h2 className="text-3xl font-bold text-[rgba(255,252,239,1)] mb-[11px]">Featured</h2>
              <FlyingTabs />
            </section>

            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[rgba(255,252,239,1)]">AI Interior Design Tools</h2>
                <p className="text-muted-foreground text-sm md:text-base">
                  {designTools?.length} tools available
                </p>
              </div>

              {/* Category Tabs - All Devices - Plain Text */}
              <div className="flex gap-6 mb-6 overflow-x-auto pb-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-2 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 text-[rgba(255,252,239,1)] ${
                  selectedCategory === 'all' ? 'text-orange-500' : 'text-white/70 hover:text-white'}`
                  }>

                  All
                </button>
                <button
                  onClick={() => setSelectedCategory('Interior Concept')}
                  className={`px-2 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 text-[rgba(255,252,239,1)] ${
                  selectedCategory === 'Interior Concept' ? 'text-orange-500' : 'text-white/70 hover:text-white'}`
                  }>

                  Interior Concept
                </button>
                <button
                  onClick={() => setSelectedCategory('Detail Editing')}
                  className={`px-2 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === 'Detail Editing' ? 'text-orange-500' : 'text-white/70 hover:text-white'}`
                  }>

                  Detail Editing
                </button>
                <button
                  onClick={() => setSelectedCategory('Architecture')}
                  className={`px-2 py-2 text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === 'Architecture' ? 'text-orange-500' : 'text-white/70 hover:text-white'}`
                  }>

                  Architecture
                </button>
              </div>

              {/* Mobile: 2-column CSS grid (cards are short, no expand issue) */}
              <div className="grid grid-cols-2 gap-3 lg:hidden">
                {filteredTools?.map((tool) => (
                  <div key={tool?.id}>
                    <ToolCard tool={tool} />
                  </div>
                ))}
              </div>

              {/* Desktop: 3 independent columns so expanding a card only pushes down cards in the same column */}
              <div className="hidden lg:flex gap-5">
                {[0, 1, 2].map((colIndex) => (
                  <div key={colIndex} className="flex-1 flex flex-col gap-5">
                    {filteredTools
                      ?.filter((_, i) => i % 3 === colIndex)
                      .map((tool, rowInCol) => (
                        <div key={tool?.id} className={rowInCol > 0 ? 'mt-3' : ''}>
                          <ToolCard tool={tool} />
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );

};

export default MainDashboard;
